import { Area } from 'react-easy-crop/types';

import { MAX_IMAGE_SIZE } from '../constants';

export const isValidFileSize = (
  file: File,
  maxSizeMB = MAX_IMAGE_SIZE
): boolean => {
  const maxAllowedSize = maxSizeMB * 1024 * 1024;
  return file.size <= maxAllowedSize;
};

export const isValidImageRatio = (
  image: HTMLImageElement,
  ratioTolerance = 0.05
): boolean => Math.abs(image.width / image.height - 16 / 9) < ratioTolerance;

export const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin
    image.src = url;
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
  });

export const getCroppedImg = async (
  image: File,
  pixelCrop: Area
): Promise<File | null> => {
  const newImage = await createImage(URL.createObjectURL(image));
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const maxSize = Math.max(newImage.width, newImage.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  if (ctx) {
    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // draw rotated image and store data.
    ctx.drawImage(
      newImage,
      safeArea / 2 - newImage.width * 0.5,
      safeArea / 2 - newImage.height * 0.5
    );

    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image with correct offsets for x,y crop values.
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + newImage.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + newImage.height * 0.5 - pixelCrop.y)
    );

    return new Promise((resolve) => {
      const dataUrl = canvas.toDataURL('image/jpeg');

      fetch(dataUrl)
        .then((res) => res.blob()) // Gets the response and returns it as a blob
        .then((blob) => {
          // Converts the blob to a file and resolve it
          const file = new File([blob], image.name, blob);
          resolve(file);
        });
    });
  }

  return new Promise((resolve) => {
    resolve(null);
  });
};
